import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type {
  Location,
  OneTimeExpenseRecord,
  OperatingCostsSummary,
  RecurringCostCategory,
  RecurringCostRecord,
} from "../../api/types";
import { useAuth } from "../../state/AuthContext";
import { Modal } from "../Modal";
import { Chip } from "../ui/Chip";
import { EmptyState } from "../ui/EmptyState";
import { Select } from "../ui/Select";
import { StatTile } from "../ui/StatTile";
import { PhotoViewer } from "../ui/PhotoViewer";
import { ReasonModal } from "../ui/ReasonModal";
import { MonthNav, monthLabel } from "./MonthNav";
import { AmountReadBack } from "./AmountReadBack";
import { ExpenseRow } from "./ExpenseRow";
import { ExpenseSheet } from "./ExpenseSheet";
import { euro, formatDayLabel } from "./opcostFormat";
import { businessToday } from "../../utils/businessDay";
import { parseTypedAmount } from "../../utils/typedAmount";

/**
 * G-32 (monthly fixed costs + the daily nut) and G-39 (one-off spend), the
 * cost half of the Finances page.
 *
 * OD-16 shows up here as a deliberate visual separation: the break-even tile
 * is labelled "recurring only" and one-off spend sits in its own tile and its
 * own card, because the owner must be able to read "we needed €X and made €Y"
 * without a fridge distorting it.
 */

const RECURRING_CATEGORY_LABEL: Record<RecurringCostCategory, string> = {
  RENT: "Rent",
  UTILITIES: "Utilities",
  CONNECTIVITY: "Internet & phone",
  INSURANCE: "Insurance",
  SOFTWARE: "Software",
  OTHER: "Other",
};

const RECURRING_CATEGORIES = Object.keys(
  RECURRING_CATEGORY_LABEL,
) as RecurringCostCategory[];

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function monthBounds(year: number, month1to12: number): { from: string; to: string } {
  const lastDay = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  return {
    from: `${year}-${pad2(month1to12)}-01`,
    to: `${year}-${pad2(month1to12)}-${pad2(lastDay)}`,
  };
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export function OperatingCostsSection() {
  const { user, hasRole } = useAuth();
  const isOwner = hasRole("OWNER");

  // G-126 ([145]): the Barcelona month, the same clock as the server and the
  // Salary section above (this used to be the browser's own day).
  const today = useMemo(() => businessToday(), []);
  const [periodYear, setPeriodYear] = useState(today.year);
  const [periodMonth, setPeriodMonth] = useState(today.month);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);

  const [summary, setSummary] = useState<OperatingCostsSummary | null>(null);
  const [recurring, setRecurring] = useState<RecurringCostRecord[]>([]);
  const [expenses, setExpenses] = useState<OneTimeExpenseRecord[]>([]);
  const [expenseTotal, setExpenseTotal] = useState("0.00");
  /** Owner only: every expense still without an invoice (all shops, all months). */
  const [missingInvoice, setMissingInvoice] = useState(0);
  /** Owner: list the expenses with no invoice, across every month, instead of one month. */
  const [onlyMissing, setOnlyMissing] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [recurringFormOpen, setRecurringFormOpen] = useState(false);
  const [recurringEditing, setRecurringEditing] =
    useState<RecurringCostRecord | null>(null);
  const [expenseFormOpen, setExpenseFormOpen] = useState(false);
  const [expenseEditing, setExpenseEditing] =
    useState<OneTimeExpenseRecord | null>(null);
  // OD-177: "No invoice will come" on one expense (owner only).
  const [markingNoInvoice, setMarkingNoInvoice] = useState<OneTimeExpenseRecord | null>(null);
  const [noInvoiceBusy, setNoInvoiceBusy] = useState(false);
  const [noInvoiceError, setNoInvoiceError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<
    { kind: "recurring" | "one-time"; id: string; label: string } | null
  >(null);
  // G-39: the receipt photo, streamed auth-gated → object URL in a modal.
  const [receiptView, setReceiptView] = useState<{
    label: string;
    url: string;
  } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const shopSelectId = useId();

  useEffect(() => {
    if (!isOwner) return;
    void (async () => {
      try {
        const list = await api.listLocations();
        setLocations(list.filter((l) => l.isActive));
      } catch {
        setLocations([]);
      }
    })();
  }, [isOwner]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const loc = isOwner && locationId ? locationId : undefined;
    const { from, to } = monthBounds(periodYear, periodMonth);
    try {
      // OD-174: a manager's page never asks for the cost summary or the monthly
      // costs — those cards are not theirs (the routes themselves stay open).
      const [summaryData, recurringData, expenseData] = await Promise.all([
        isOwner
          ? api.getOperatingCostsSummary({
              year: periodYear,
              month: periodMonth,
              locationId: loc,
            })
          : Promise.resolve(null),
        isOwner
          ? api.listRecurringCosts({
              locationId: loc,
              activeInMonthOf: from,
            })
          : Promise.resolve(null),
        isOwner && onlyMissing
          ? api.listOneTimeExpenses({ withoutInvoice: true })
          : api.listOneTimeExpenses({ locationId: loc, from, to }),
      ]);
      setSummary(summaryData);
      setRecurring(recurringData?.rows ?? []);
      setExpenses(expenseData.rows);
      setExpenseTotal(expenseData.totalEur);
    } catch (err: unknown) {
      setSummary(null);
      setRecurring([]);
      setExpenses([]);
      setExpenseTotal("0.00");
      setError(errorMessage(err, "Could not load operating costs"));
    } finally {
      setLoading(false);
    }
  }, [isOwner, locationId, periodYear, periodMonth, onlyMissing]);

  // The count does not depend on the month or shop: it is read on its own, and
  // again only when an expense or invoice is added or removed.
  const loadMissing = useCallback(async () => {
    if (!isOwner) return;
    try {
      const { count } = await api.getExpensesMissingInvoiceCount();
      setMissingInvoice(count);
      if (count === 0) setOnlyMissing(false);
    } catch {
      setMissingInvoice(0);
    }
  }, [isOwner]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void loadMissing();
  }, [loadMissing]);

  const refreshAll = useCallback(async () => {
    await Promise.all([refresh(), loadMissing()]);
  }, [refresh, loadMissing]);

  const markNoInvoice = async (reason: string | undefined) => {
    if (!markingNoInvoice || !reason) return;
    setNoInvoiceBusy(true);
    setNoInvoiceError(null);
    try {
      await api.markOneTimeExpenseNoInvoice(markingNoInvoice.id, reason);
      setMarkingNoInvoice(null);
      await refreshAll();
    } catch (err: unknown) {
      setNoInvoiceError(err instanceof Error ? err.message : "Could not save that");
    } finally {
      setNoInvoiceBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      if (deleting.kind === "recurring") {
        await api.deleteRecurringCost(deleting.id);
      } else {
        await api.deleteOneTimeExpense(deleting.id);
      }
      setDeleting(null);
      await refreshAll();
    } catch (err: unknown) {
      setError(errorMessage(err, "Could not delete"));
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const managerShopLabel =
    !isOwner && user?.assignedLocation
      ? `${user.assignedLocation.name} (${user.assignedLocation.code})`
      : null;

  const scopeLabel = isOwner
    ? locationId
      ? (locations.find((l) => l.id === locationId)?.name ?? "one shop")
      : "all shops"
    : (managerShopLabel ?? "your shop");

  return (
    <>
      {/* OD-174: managers see only the one-off card — the two cost cards are the owner's */}
      {isOwner ? (
        <>
          <div className="card opcost-card">
            <div className="opcost-card-head">
              <div>
                <h3 className="opcost-card-title">Running costs &amp; break-even</h3>
                <p className="opcost-card-subtitle">
                  What the shop pays every month — rent, electricity, water, internet
                  — and what that works out to per day. This is the cost side only;
                  flowers and wages are counted elsewhere.
                </p>
              </div>
            </div>

            <div className="opcost-controls">
              <MonthNav
                year={periodYear}
                month={periodMonth}
                onChange={(y, m) => {
                  setPeriodYear(y);
                  setPeriodMonth(m);
                }}
              />
              {isOwner ? (
                <div className="field opcost-shop-field">
                  <label htmlFor={shopSelectId}>Shop</label>
                  <Select
                    id={shopSelectId}
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    <option value="">All shops</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </option>
                    ))}
                  </Select>
                </div>
              ) : managerShopLabel ? (
                <p className="muted opcost-scope-note">
                  Shop: <strong>{managerShopLabel}</strong>
                </p>
              ) : (
                <p className="muted opcost-scope-note">
                  No shop assignment — costs unavailable.
                </p>
              )}
            </div>

            {error ? <div className="alert error">{error}</div> : null}

            <div className="stat-tile-row">
              <StatTile
                label="Monthly bills this month"
                value={loading ? "…" : euro(summary?.monthlyFixedCostEur)}
                hint={`Rent, utilities and the rest · counted in full · ${scopeLabel}`}
              />
              <StatTile
                label="Must clear per day"
                value={loading ? "…" : euro(summary?.dailyFixedCostEur)}
                tone="warning"
                hint={
                  summary
                    ? `${euro(summary.monthlyFixedCostEur)} ÷ ${summary.tradingDaysInMonth} calendar days · monthly bills only`
                    : "The daily break-even baseline"
                }
              />
              <StatTile
                label="Running costs today"
                value={loading ? "…" : euro(summary?.today.totalCostEur)}
                hint={
                  summary?.today.isInPeriod
                    ? `${euro(summary.today.fixedCostEur)} bills per day + ${euro(summary.today.oneTimeCostEur)} one-off`
                    : "Today is outside the month shown"
                }
              />
              <StatTile
                label="One-off spend this month"
                value={loading ? "…" : euro(summary?.oneTimeTotalEur)}
                tone="negative"
                hint="On its own day · not in the daily figure"
              />
            </div>

            {isOwner && !locationId && summary && summary.byLocation.length > 0 ? (
              <div className="opcost-bylocation">
                <h4 className="opcost-subheading">Per shop</h4>
                <div className="table-scroll" tabIndex={0} role="region" aria-label="Costs per shop">
                  <table className="table opcost-table">
                    <thead>
                      <tr>
                        <th scope="col">Shop</th>
                        <th scope="col">Fixed / month</th>
                        <th scope="col">Per day</th>
                        <th scope="col">One-off</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.byLocation.map((row) => (
                        <tr key={row.locationId}>
                          <td>
                            <strong>{row.locationName}</strong>{" "}
                            <span className="muted">({row.locationCode})</span>
                          </td>
                          <td>{euro(row.monthlyFixedCostEur)}</td>
                          <td>{euro(row.dailyFixedCostEur)}</td>
                          <td>{euro(row.oneTimeTotalEur)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>

          {/* ── G-32: the recurring cost list ─────────────────────────────────── */}
          <div className="card opcost-card">
            <div className="opcost-card-head">
              <div>
                <h3 className="opcost-card-title">Monthly costs</h3>
                <p className="opcost-card-subtitle">
                  Showing what was being paid in {monthLabel(periodYear, periodMonth)}.
                  A cost counts its full monthly amount in every month it touches, even
                  by one day. When rent changes, end the old line on the last day of the
                  month before and add a new one — that way last year&apos;s figures stay right.
                </p>
              </div>
              {isOwner ? (
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setRecurringEditing(null);
                    setRecurringFormOpen(true);
                  }}
                >
                  Add monthly cost
                </button>
              ) : null}
            </div>

            {loading ? (
              <p className="muted opcost-loading">Loading…</p>
            ) : recurring.length === 0 ? (
              <EmptyState
                title="No monthly costs recorded"
                message={
                  isOwner
                    ? "Add rent, electricity, water and internet so the break-even figure means something."
                    : "The owner has not recorded this shop's monthly costs yet."
                }
                action={
                  isOwner ? (
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        setRecurringEditing(null);
                        setRecurringFormOpen(true);
                      }}
                    >
                      Add monthly cost
                    </button>
                  ) : undefined
                }
              />
            ) : (
              <ul className="opcost-list">
                {recurring.map((row) => (
                  <li key={row.id} className="opcost-row">
                    <div className="opcost-row-main">
                      <span className="opcost-row-title">
                        {row.name}
                        <span className="opcost-row-amount">
                          {euro(row.monthlyAmountEur)}/month
                        </span>
                      </span>
                      <span className="opcost-row-meta">
                        {RECURRING_CATEGORY_LABEL[row.category]} · {row.location.name} ·
                        from {formatDayLabel(row.activeFrom)}
                        {row.activeTo ? ` until ${formatDayLabel(row.activeTo)}` : ""}
                      </span>
                    </div>
                    {row.activeTo ? (
                      <span className="opcost-badge">Ended</span>
                    ) : null}
                    {isOwner ? (
                      <div className="opcost-row-actions">
                        <button
                          type="button"
                          className="button plain"
                          onClick={() => {
                            setRecurringEditing(row);
                            setRecurringFormOpen(true);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button plain"
                          onClick={() =>
                            setDeleting({
                              kind: "recurring",
                              id: row.id,
                              label: `${row.name} — ${euro(row.monthlyAmountEur)}/month`,
                            })
                          }
                        >
                          Delete
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}

      {/* ── G-39: one-off expenses ───────────────────────────────────────── */}
      <div className="card opcost-card">
        <div className="opcost-card-head">
          <div>
            <h3 className="opcost-card-title">One-off expenses</h3>
          </div>
          <button
            type="button"
            className="button"
            onClick={() => {
              setExpenseEditing(null);
              setExpenseFormOpen(true);
            }}
          >
            Add expense
          </button>
        </div>

        {isOwner && missingInvoice > 0 ? (
          <div className="alert warning opcost-missing-invoice" role="status">
            <span>
              {missingInvoice} {missingInvoice === 1 ? "expense has" : "expenses have"} no invoice
              (all shops, all months).
            </span>
            <button
              type="button"
              className="button plain"
              onClick={() => setOnlyMissing((v) => !v)}
            >
              {onlyMissing ? "Back to the month" : "Show them"}
            </button>
          </div>
        ) : null}

        {isOwner ? null : (
          <div className="opcost-controls">
            <MonthNav
              year={periodYear}
              month={periodMonth}
              onChange={(y, m) => {
                setPeriodYear(y);
                setPeriodMonth(m);
              }}
            />
          </div>
        )}

        {!isOwner && error ? <div className="alert error">{error}</div> : null}

        {loading ? (
          <p className="muted opcost-loading">Loading…</p>
        ) : expenses.length === 0 ? (
          <EmptyState
            title="Nothing one-off this month"
            message={`No one-off spend recorded in ${monthLabel(periodYear, periodMonth)} for ${scopeLabel}.`}
          />
        ) : (
          <>
            <p className="opcost-total">
              <span className="muted">{onlyMissing ? "Total without an invoice" : "Total this month"}</span>{" "}
              <strong>{euro(expenseTotal)}</strong>
            </p>
            <ul className="opcost-list">
              {expenses.map((row) => (
                <ExpenseRow
                  key={row.id}
                  row={row}
                  isOwner={isOwner}
                  showWhere={isOwner && !locationId}
                  onViewReceipt={(r) => {
                    void api.fetchOneTimeExpenseReceiptBlobUrl(r.id).then((url) => {
                      if (url) {
                        setReceiptView({
                          label: `${r.description} — ${euro(r.amountEur)}`,
                          url,
                        });
                      }
                    });
                  }}
                  onEdit={(r) => {
                    setExpenseEditing(r);
                    setExpenseFormOpen(true);
                  }}
                  onDelete={(r) =>
                    setDeleting({
                      kind: "one-time",
                      id: r.id,
                      label: `${r.description} — ${euro(r.amountEur)}`,
                    })
                  }
                  onInvoiceAttached={refreshAll}
                  onMarkNoInvoice={(r) => {
                    setNoInvoiceError(null);
                    setMarkingNoInvoice(r);
                  }}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      {receiptView ? (
        <PhotoViewer
          title={receiptView.label}
          url={receiptView.url}
          alt="Receipt"
          onClose={() => {
            URL.revokeObjectURL(receiptView.url);
            setReceiptView(null);
          }}
        />
      ) : null}

      {recurringFormOpen ? (
        <RecurringCostModal
          existing={recurringEditing}
          locations={locations}
          isOwner={isOwner}
          fallbackLocationId={user?.assignedLocation?.id ?? ""}
          defaultMonth={{ year: periodYear, month: periodMonth }}
          onClose={() => {
            setRecurringFormOpen(false);
            setRecurringEditing(null);
          }}
          onSaved={async () => {
            setRecurringFormOpen(false);
            setRecurringEditing(null);
            await refresh();
          }}
        />
      ) : null}

      {expenseFormOpen ? (
        <ExpenseSheet
          existing={expenseEditing}
          locations={locations}
          isOwner={isOwner}
          fallbackLocation={user?.assignedLocation ?? null}
          onClose={() => {
            setExpenseFormOpen(false);
            setExpenseEditing(null);
          }}
          onSaved={async () => {
            setExpenseFormOpen(false);
            setExpenseEditing(null);
            await refreshAll();
          }}
        />
      ) : null}

      {/* OD-177: owner only — the expense leaves the "no invoice" warning, with a reason. */}
      {markingNoInvoice ? (
        <ReasonModal
          title="No invoice will come?"
          message={`${markingNoInvoice.description} — ${euro(markingNoInvoice.amountEur)}. It leaves the "no invoice" warning and the home page's count. Who, when and why stay on the row. An invoice attached later still goes on.`}
          reasonMode="required"
          reasonLabel="Why will no invoice come?"
          reasonPlaceholder="e.g. market stall, paid cash"
          minReasonLength={3}
          confirmLabel="No invoice will come"
          busy={noInvoiceBusy}
          error={noInvoiceError}
          onConfirm={markNoInvoice}
          onClose={() => setMarkingNoInvoice(null)}
        />
      ) : null}

      {deleting ? (
        <ReasonModal
          title="Delete this line?"
          message={`${deleting.label}. This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          busy={deleteBusy}
          onConfirm={() => void confirmDelete()}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </>
  );
}

// ─────────────────────────── Recurring cost form (G-32) ───────────────────────────

function RecurringCostModal({
  existing,
  locations,
  isOwner,
  fallbackLocationId,
  defaultMonth,
  onClose,
  onSaved,
}: {
  existing: RecurringCostRecord | null;
  locations: Location[];
  isOwner: boolean;
  fallbackLocationId: string;
  defaultMonth: { year: number; month: number };
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [locationId, setLocationId] = useState(
    existing?.locationId ?? (isOwner ? (locations[0]?.id ?? "") : fallbackLocationId),
  );
  const [name, setName] = useState(existing?.name ?? "");
  const [amount, setAmount] = useState(existing?.monthlyAmountEur ?? "");
  const [category, setCategory] = useState<RecurringCostCategory>(
    existing?.category ?? "RENT",
  );
  const [activeFrom, setActiveFrom] = useState(
    existing?.activeFrom ?? monthBounds(defaultMonth.year, defaultMonth.month).from,
  );
  const [activeTo, setActiveTo] = useState(existing?.activeTo ?? "");
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const ids = useId();

  useEffect(() => {
    if (!locationId && isOwner && locations.length > 0) {
      setLocationId(locations[0].id);
    }
  }, [locationId, isOwner, locations]);

  const submit = async () => {
    setLocalError(null);
    if (!locationId) {
      setLocalError("Choose which shop this cost belongs to.");
      return;
    }
    if (name.trim().length < 2) {
      setLocalError("Give the cost a name, e.g. Rent or Electricity.");
      return;
    }
    const read = parseTypedAmount(amount);
    if (!read.ok || read.value <= 0) {
      setLocalError(read.ok ? "Enter the monthly amount, e.g. 1800." : read.message);
      return;
    }
    const parsed = read.value;
    if (!activeFrom) {
      setLocalError("Choose the first month this amount applies to.");
      return;
    }
    if (activeTo && activeTo < activeFrom) {
      setLocalError("The end date must be on or after the start date.");
      return;
    }
    setSaving(true);
    try {
      if (existing) {
        await api.updateRecurringCost(existing.id, {
          locationId,
          name: name.trim(),
          monthlyAmountEur: parsed,
          category,
          activeFrom,
          activeTo: activeTo || null,
        });
      } else {
        await api.createRecurringCost({
          locationId,
          name: name.trim(),
          monthlyAmountEur: parsed,
          category,
          activeFrom,
          ...(activeTo ? { activeTo } : {}),
        });
      }
      await onSaved();
    } catch (err: unknown) {
      setLocalError(errorMessage(err, "Could not save this cost"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={existing ? "Edit monthly cost" : "Add a monthly cost"}
      onClose={onClose}
    >
      <div className="opcost-form">
        <p className="muted opcost-form-lead">
          Example: <strong>Rent — €1,800 per month — Sant Joan</strong>.
        </p>
        {localError ? <div className="alert error">{localError}</div> : null}

        {isOwner ? (
          <div className="field">
            <label htmlFor={`${ids}-shop`}>Which shop</label>
            <Select
              id={`${ids}-shop`}
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor={`${ids}-name`}>What is it</label>
          <input
            id={`${ids}-name`}
            type="text"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Rent"
          />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-amount`}>How much per month (EUR)</label>
          <input
            id={`${ids}-amount`}
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1800"
          />
          <AmountReadBack raw={amount} />
        </div>

        <div className="field">
          <span className="opcost-chip-label">Type of cost</span>
          <div className="opcost-chip-row" role="group" aria-label="Type of cost">
            {RECURRING_CATEGORIES.map((value) => (
              <Chip
                key={value}
                label={RECURRING_CATEGORY_LABEL[value]}
                selected={category === value}
                onClick={() => setCategory(value)}
              />
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor={`${ids}-from`}>Paying this since</label>
          <input
            id={`${ids}-from`}
            type="date"
            value={activeFrom}
            onChange={(e) => setActiveFrom(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-to`}>Last day it counts (included; leave empty if ongoing)</label>
          <input
            id={`${ids}-to`}
            type="date"
            value={activeTo}
            onChange={(e) => setActiveTo(e.target.value)}
          />
          <p className="field-hint">
            The cost counts its full monthly amount in every month it touches, even by
            one day. When a price changes, end this line on the last day of the month
            before and add a new line with the new amount. Old months keep the old figure.
          </p>
        </div>

        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void submit()}
            disabled={saving}
          >
            {saving ? "Saving…" : existing ? "Save changes" : "Add cost"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
